/**
 * mSTEM Robot Block Info Modal — presentational "Thông tin khối Robot"
 * (DR-05 / ROB-02..04). Hosts one of the 3 static composite "what's
 * inside" preview SVGs (line-follow / avoid-obstacle / follow-object)
 * plus a single "Đã hiểu" primary action.
 *
 * Pure dumb component: the composite to show is selected by `compositeKey`
 * (one of 'lineFollow' | 'avoidObstacle' | 'followObject'); it always maps
 * to a valid SVG (defaults to lineFollow) so the modal is never empty
 * (UI-SPEC Pitfall 4: never-empty surface). Close is dispatched by the
 * container via onRequestClose.
 */
import bindAll from 'lodash.bindall';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';

import lineFollowSvg from '../robot-composite-preview/line-follow.svg';
import avoidObstacleSvg from '../robot-composite-preview/avoid-obstacle.svg';
import followObjectSvg from '../robot-composite-preview/follow-object.svg';

import styles from './robot-block-info-modal.css';

const messages = defineMessages({
    title: {
        defaultMessage: 'Thông tin khối Robot',
        description: 'Title of the Robot block info modal (DR-05)',
        id: 'gen.robot.info.title'
    },
    whatsInside: {
        defaultMessage: 'Khối này gồm:',
        description: 'Section head introducing the composite "what\'s inside" diagram',
        id: 'gen.robot.info.whatsInside'
    },
    gotIt: {
        defaultMessage: 'Đã hiểu',
        description: 'Primary button that closes the Robot block info modal',
        id: 'gen.robot.info.gotIt'
    },
    tabLineFollow: {
        defaultMessage: 'Dò đường',
        description: 'Composite selector tab: line-follow (ROB-02)',
        id: 'gen.robot.info.tabLineFollow'
    },
    tabAvoidObstacle: {
        defaultMessage: 'Tránh vật cản',
        description: 'Composite selector tab: avoid-obstacle (ROB-03)',
        id: 'gen.robot.info.tabAvoidObstacle'
    },
    tabFollowObject: {
        defaultMessage: 'Bám theo vật',
        description: 'Composite selector tab: follow-object (ROB-04)',
        id: 'gen.robot.info.tabFollowObject'
    }
});

const COMPOSITE_SVGS = {
    lineFollow: lineFollowSvg,
    avoidObstacle: avoidObstacleSvg,
    followObject: followObjectSvg
};

// The 3 selectable composites, in the DR-02 palette order.
const COMPOSITE_TABS = [
    {key: 'lineFollow', label: messages.tabLineFollow},
    {key: 'avoidObstacle', label: messages.tabAvoidObstacle},
    {key: 'followObject', label: messages.tabFollowObject}
];

// Small class child so each tab carries a STABLE bound click handler (avoids
// react/jsx-no-bind in the .map; matches the React-16 lodash.bindall pattern).
class RobotCompositeTab extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, ['handleClick']);
    }
    handleClick () {
        this.props.onSelect(this.props.tabKey);
    }
    render () {
        const {active, label, tabKey} = this.props;
        return (
            <button
                type="button"
                role="tab"
                aria-selected={active}
                className={active ? classNames(styles.tab, styles.tabActive) : styles.tab}
                data-composite-tab={tabKey}
                onClick={this.handleClick}
            >
                <FormattedMessage {...label} />
            </button>
        );
    }
}

RobotCompositeTab.propTypes = {
    active: PropTypes.bool.isRequired,
    label: PropTypes.shape({
        defaultMessage: PropTypes.string,
        description: PropTypes.string,
        id: PropTypes.string
    }).isRequired,
    onSelect: PropTypes.func.isRequired,
    tabKey: PropTypes.string.isRequired
};

const RobotBlockInfoModalComponent = ({intl, compositeKey, onRequestClose, onSelectComposite}) => {
    const activeKey = COMPOSITE_SVGS[compositeKey] ? compositeKey : 'lineFollow';
    const diagram = COMPOSITE_SVGS[activeKey];
    return (
        <Modal
            className={styles.modalContent}
            contentLabel={intl.formatMessage(messages.title)}
            headerClassName={styles.header}
            id="robotBlockInfoModal"
            onRequestClose={onRequestClose}
        >
            <div className={styles.body}>
                {/* Phase 4 gap-closure: composite selector — "Xem bên trong"
                    for each of the 3 composites (ROB-02/03/04). */}
                <div
                    className={styles.tabRow}
                    role="tablist"
                >
                    {COMPOSITE_TABS.map(tab => (
                        <RobotCompositeTab
                            key={tab.key}
                            tabKey={tab.key}
                            label={tab.label}
                            active={activeKey === tab.key}
                            onSelect={onSelectComposite}
                        />
                    ))}
                </div>
                <p className={styles.sectionHead}>
                    <FormattedMessage {...messages.whatsInside} />
                </p>
                <div className={styles.figure}>
                    <img
                        className={styles.illustration}
                        src={diagram}
                        alt={intl.formatMessage(messages.title)}
                        data-composite-key={activeKey}
                    />
                </div>
                <div className={styles.footer}>
                    <button
                        type="button"
                        className={styles.gotItButton}
                        onClick={onRequestClose}
                    >
                        <FormattedMessage {...messages.gotIt} />
                    </button>
                </div>
            </div>
        </Modal>
    );
};

RobotBlockInfoModalComponent.propTypes = {
    compositeKey: PropTypes.oneOf(['lineFollow', 'avoidObstacle', 'followObject']),
    intl: intlShape.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onSelectComposite: PropTypes.func.isRequired
};

RobotBlockInfoModalComponent.defaultProps = {
    compositeKey: 'lineFollow'
};

export default injectIntl(RobotBlockInfoModalComponent);
